import { useCallback, useEffect, useState } from 'react';
import { engineFetchJson } from '@/lib/engineHttp';

interface GovernanceViolation {
  id: string;
  event_type: string;
  result_state: string;
  reason: string;
  created_at: string | null;
}

interface GovernanceSummary {
  canon_exemplar_count: number;
  canon_anti_exemplar_count: number;
  canon_categories: Record<string, number>;
  governance_event_count_30d: number;
  governance_events_by_type: Record<string, number>;
  recent_violations: GovernanceViolation[];
}

export function CouncilTab() {
  const [data, setData] = useState<GovernanceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const summary = await engineFetchJson<GovernanceSummary>('/governance/summary');
      setData(summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch governance summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !data) {
    return (
      <div className="p-6 font-mono text-xs text-gray-500 animate-pulse">
        &gt; CONNECTING TO GOVERNANCE STANDARDS CHANNELS...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 font-mono text-xs text-red-400 space-y-2">
        <div className="text-xs uppercase tracking-wider text-gray-600 mb-2">Governance Sync Failure</div>
        <p>&gt; ERROR: Could not resolve Council/Governor summaries from engine.</p>
        {error && <p className="text-gray-500">&gt; {error}</p>}
      </div>
    );
  }

  const {
    canon_exemplar_count,
    canon_anti_exemplar_count,
    canon_categories = {},
    governance_event_count_30d,
    governance_events_by_type = {},
    recent_violations = [],
  } = data;

  return (
    <div className="p-6 font-mono text-sm text-gray-200 space-y-6 overflow-auto h-full max-h-[calc(100vh-140px)]">
      <div className="flex items-baseline justify-between border-b border-gray-800 pb-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500">AI Council & Standards</div>
          <div className="text-lg font-bold text-white mt-1">Governor Policy Attestation</div>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-xs text-gray-500">STATUS: <span className="text-[#39FF14]">LIVE</span></span>
          <button
            onClick={() => void load()}
            disabled={loading}
            className="px-2 py-1 border border-gray-800 rounded hover:border-[#39FF14]/40 hover:text-[#39FF14] transition-colors disabled:opacity-50 text-xs"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Summary KPI Grid */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-gray-900/40 border border-gray-800 rounded p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Canon Exemplars</div>
          <div className="text-2xl font-bold text-gray-100 mt-1">{canon_exemplar_count.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">approved behavior examples</div>
        </div>
        <div className="bg-gray-900/40 border border-gray-800 rounded p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Anti-Exemplars</div>
          <div className="text-2xl font-bold text-yellow-500 mt-1">{canon_anti_exemplar_count.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">prohibited action definitions</div>
        </div>
        <div className="bg-gray-900/40 border border-gray-800 rounded p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">30d Events Audited</div>
          <div className="text-2xl font-bold text-blue-400 mt-1">{governance_event_count_30d.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">transactions evaluated</div>
        </div>
        <div className="bg-gray-900/40 border border-gray-800 rounded p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Rules Active</div>
          <div className="text-2xl font-bold text-[#39FF14] mt-1">{Object.keys(canon_categories).length}</div>
          <div className="text-xs text-gray-500 mt-1">active rule categories</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Canon Category Distribution */}
        <div className="bg-gray-900/20 border border-gray-800 rounded p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-3">Canon Categories</div>
          {Object.keys(canon_categories).length === 0 ? (
            <div className="text-xs text-gray-500 py-4 text-center">No categories indexed in Canon database.</div>
          ) : (
            <div className="space-y-2">
              {Object.entries(canon_categories).map(([category, count]) => {
                const total = canon_exemplar_count + canon_anti_exemplar_count || 1;
                const pct = (count / total) * 100;
                return (
                  <div key={category} className="text-xs space-y-1">
                    <div className="flex justify-between items-baseline">
                      <span className="font-bold text-gray-400 uppercase tracking-wider">{category.replace(/_/g, ' ')}</span>
                      <span className="text-gray-400">{count} ({pct.toFixed(0)}%)</span>
                    </div>
                    <div className="w-full bg-gray-900 h-1.5 rounded overflow-hidden">
                      <div className="h-full bg-[#39FF14]/50" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Governance Event Types */}
        <div className="bg-gray-900/20 border border-gray-800 rounded p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-3">Event Type Distribution (30d)</div>
          {Object.keys(governance_events_by_type).length === 0 ? (
            <div className="text-xs text-gray-500 py-4 text-center">No recent governance events recorded.</div>
          ) : (
            <div className="space-y-2">
              {Object.entries(governance_events_by_type).map(([type, count]) => {
                const total = governance_event_count_30d || 1;
                const pct = (count / total) * 100;
                return (
                  <div key={type} className="text-xs space-y-1">
                    <div className="flex justify-between items-baseline">
                      <span className="font-bold text-gray-400 capitalize">{type.replace(/_/g, ' ')}</span>
                      <span className="text-gray-400">{count} ({pct.toFixed(0)}%)</span>
                    </div>
                    <div className="w-full bg-gray-900 h-1.5 rounded overflow-hidden">
                      <div className="h-full bg-blue-500/50" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Recent Violations Log */}
      <div className="bg-gray-900/40 border border-gray-800 rounded p-4">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-3">Recent Governance Logs</div>
        {recent_violations.length === 0 ? (
          <p className="text-gray-500 text-xs py-2">No policy violation incidents logged.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500 uppercase tracking-wider text-left border-b border-gray-800">
                <tr>
                  <th className="py-2 font-normal">Audit Event</th>
                  <th className="py-2 font-normal">Created At</th>
                  <th className="py-2 font-normal">Result State</th>
                  <th className="py-2 font-normal">Violation Reason / Rationale</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {recent_violations.map((v) => (
                  <tr key={v.id} className="hover:bg-gray-800/10">
                    <td className="py-3 pr-4">
                      <span className="font-bold text-gray-200 uppercase tracking-wider block">{v.event_type.replace(/_/g, ' ')}</span>
                      <span className="text-[10px] text-gray-500 block">{v.id.slice(0, 8)}...</span>
                    </td>
                    <td className="py-3 text-gray-400 whitespace-nowrap">
                      {v.created_at ? new Date(v.created_at).toLocaleString() : '—'}
                    </td>
                    <td className="py-3">
                      <span className={`px-1.5 py-0.5 border text-[10px] font-bold rounded uppercase tracking-wider ${
                        v.result_state?.toLowerCase() === 'approved' || v.result_state?.toLowerCase() === 'pass'
                          ? 'text-[#39FF14] border-[#39FF14]/20 bg-[#39FF14]/5'
                          : 'text-[#FF0055] border-[#FF0055]/20 bg-[#FF0055]/5'
                      }`}>
                        {v.result_state || 'VIOLATION'}
                      </span>
                    </td>
                    <td className="py-3 text-gray-400 max-w-xl leading-relaxed">{v.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
